import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faCircleNodes, faUserCheck, faUserXmark } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import EstadoSelector from './EstadoSelector';
import { formatearFecha } from './utils/fechas';

const formatearDomicilio = (domicilio) => {
  if (!domicilio) return "";

  const texto = domicilio.toString().trim();
  const pareceCoordenada =
    /^lat\s*:/i.test(texto) ||
    /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?/.test(texto);

  return pareceCoordenada ? "Ubicacion seleccionada en mapa" : texto;
};

const personaEstaActiva = (persona) => {
  const estado = persona?.estado ?? persona?.Estado ?? persona?.EstadoPersona;
  return Number(estado) === 1;
};

const EstadoTratamientoColor = ({ nombre, estadosTratamiento = [], campo, onChange }) => {
  const texto = String(nombre || "").trim();
  const catalogo = (Array.isArray(estadosTratamiento) ? estadosTratamiento : []).filter((item) =>
    !item.CampoAplicacion || item.CampoAplicacion === "AMBOS" || item.CampoAplicacion === campo
  );
  const estado = catalogo.find((item) =>
    String(item.Nombre || "").trim().toUpperCase() === texto.toUpperCase()
  );

  const valor = estado?.IdEstadoTratamientoPorPatologia ?? (texto ? "__historico" : "");

  return (
    <select
      value={valor}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => onChange?.(event.target.value)}
      title={texto || "Sin asignar"}
      style={{
        backgroundColor: estado?.ColorAsociado || "#e5e7eb",
        border: "none",
        padding: "0.25rem 0.5rem",
        borderRadius: "0.25rem",
        fontWeight: "bold",
        minWidth: "120px",
        width: "100%",
        textAlign: "center",
        color: "#000",
        cursor: "pointer",
      }}
    >
      <option value="" style={{ backgroundColor: "#e5e7eb" }}>Sin asignar</option>
      {!estado && texto && <option value="__historico" disabled>{texto}</option>}
      {catalogo.filter((item) => Number(item.estado) === 1 && Number(item.EstadoCatalogo) === 1).map((item) => (
        <option key={item.IdEstadoTratamientoPorPatologia} value={item.IdEstadoTratamientoPorPatologia} style={{ backgroundColor: item.ColorAsociado, color: "#000" }}>
          {item.Nombre}
        </option>
      ))}
    </select>
  );
};

const navegarAGisPersona = (navigate, persona) => {
  const params = new URLSearchParams({
    panel: "persona",
    idPersona: String(persona.IdPersona ?? ""),
    nroDocumento: String(persona.NroDocumento ?? ""),
    nombre: String(persona.Nombre ?? ""),
    apellido: String(persona.Apellido ?? ""),
    edad: String(persona.Edad ?? ""),
  });

  navigate(`/PrivateMainPage/GIS?${params.toString()}`, {
    state: {
      abrirPersonaDomicilio: true,
      persona: {
        IdPersona: persona.IdPersona,
        NroDocumento: persona.NroDocumento,
        Nombre: persona.Nombre,
        Apellido: persona.Apellido,
        Edad: persona.Edad,
      },
    },
  });
};

const FilaExpandida = ({ data, personas, onEstadoChange, onEstadoTratamientoChange, onOpenModal, activarDesactivarPersona, tiposDocumento, estados, estadosTratamiento = [], fechasSeguimientoFinales }) => {
  const navigate = useNavigate();

  const dataActualizada = personas.find(p => p.IdPersona === data.IdPersona) || data;

  const subFilasData = data.tipo === 'Caso'
    ? personas.filter(p => dataActualizada.contactos?.some(c => c.IdPersona === p.IdPersona))
    : personas.filter(p => dataActualizada.casos?.some(c => c.IdPersona === p.IdPersona));

  const subFilasNombre = dataActualizada.tipo === 'Caso' ? 'contactos' : 'casos';

  return (
    <div style={{ padding: "1rem", backgroundColor: "#f9f9f9" }}>
      {subFilasData?.length > 0 ? (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.75rem",
            tableLayout: "fixed"
          }}
        >
          <thead>
            <tr style={{ background: "#f0f0f0", textAlign: "center", height: "28px" }}>
              <th style={{ width: "130px" }}>Domicilio</th>
              <th style={{ width: "100px" }}>Teléfono</th>
              <th style={{ width: "160px" }}>Eventos</th>
              <th style={{ width: "140px" }}>Diagnóstico</th>
              <th style={{ width: "100px" }}>Nombre</th>
              <th style={{ width: "100px" }}>Apellido</th>
              <th style={{ width: "90px" }}>N° Documento</th>
              <th style={{ width: "110px" }}>Tipo Documento</th>
              <th style={{ width: "50px" }}>Edad</th>
              <th style={{ width: "60px" }}>Sexo</th>
              <th style={{ width: "70px" }}>Género</th>
              <th style={{ width: "120px" }}>Prob. Exposición</th>
              <th style={{ width: "120px" }}>Inicio Síntomas</th>
              <th style={{ width: "120px" }}>Primera Consulta</th>
              <th style={{ width: "90px" }}>Estado</th>
              <th style={{ width: "150px" }}>Clasificación inicio tratamiento</th>
              <th style={{ width: "150px" }}>Resultado del tratamiento</th>
              <th style={{ width: "60px" }}>Editar</th>
              <th style={{ width: "60px" }}>Graficar</th>
              <th style={{ width: "60px" }}>GIS</th>
              <th style={{ width: "70px" }}>Activo</th>
              <th style={{ width: "90px" }}>Seguimiento</th>
            </tr>
          </thead>

          <tbody>
            {subFilasData.map((c) => {
              const personaActual = personas.find(p => p.IdPersona === c.IdPersona) || c;
              const fechas = fechasSeguimientoFinales.find((f) => f.IdPersona === personaActual.IdPersona) || {};

              return (
                <tr key={personaActual.IdPersona} style={{ textAlign: "center", height: "26px" }}>
                  <td style={{ width: "130px", overflow: "hidden", textOverflow: "ellipsis" }}>{formatearDomicilio(personaActual.Domicilio)}</td>
                  <td style={{ width: "100px" }}>{personaActual.Telefono || ""}</td>
                  <td style={{ width: "160px", overflow: "hidden", textOverflow: "ellipsis" }}>{personaActual.Eventos || ""}</td>
                  <td style={{ width: "140px", overflow: "hidden", textOverflow: "ellipsis" }}>{personaActual.Diagnostico || ""}</td>
                  <td style={{ width: "100px" }}>{personaActual.Nombre}</td>
                  <td style={{ width: "100px" }}>{personaActual.Apellido}</td>
                  <td style={{ width: "90px" }}>{personaActual.NroDocumento}</td>

                  <td style={{ width: "110px" }}>
                    {tiposDocumento.find(
                      (t) => Number(t.IdTipoDocumento ?? t.idTipoDocumento) === Number(personaActual.IdTipoDocumento ?? personaActual.idTipoDocumento)
                    )?.TipoDocumento || "No asignado"}
                  </td>

                  <td style={{ width: "50px" }}>{personaActual.Edad}</td>
                  <td style={{ width: "60px" }}>{personaActual.Sexo}</td>
                  <td style={{ width: "70px" }}>{personaActual.Genero}</td>

                  <td style={{ width: "120px" }}>{formatearFecha(fechas.FechaProbableExposicion) || "No disponible"}</td>
                  <td style={{ width: "120px" }}>{formatearFecha(fechas.FechaInicioSintomas) || "No disponible"}</td>
                  <td style={{ width: "120px" }}>{formatearFecha(fechas.FechaPrimeraConsulta) || "No disponible"}</td>

                  <td style={{ width: "90px" }}>
                    <EstadoSelector
                      value={personaActual.IdEstado}
                      estados={estados}
                      width="60px"
                      onChange={(e) => onEstadoChange(personaActual.IdPersona, Number(e.target.value), dataActualizada.IdPersona)}
                    />
                  </td>

                  <td style={{ width: "150px" }}><EstadoTratamientoColor nombre={personaActual.ClasificacionInicioTratamiento} estadosTratamiento={estadosTratamiento} campo="CLASIFICACION_INICIO" onChange={(id) => onEstadoTratamientoChange(personaActual.IdPersona, "clasificacion", id)} /></td>
                  <td style={{ width: "150px" }}><EstadoTratamientoColor nombre={personaActual.ResultadoTratamiento} estadosTratamiento={estadosTratamiento} campo="RESULTADO" onChange={(id) => onEstadoTratamientoChange(personaActual.IdPersona, "resultado", id)} /></td>

                  <td style={{ width: "60px" }}>
                    <button className="button is-small is-info" onClick={() => onOpenModal(c)}>
                      <FontAwesomeIcon icon={faPencil} />
                    </button>
                  </td>

                  <td style={{ width: "60px" }}>
                    <button className="isgraphinfo" onClick={() => navigate("/PrivateMainPage/Grafos/" + c.IdPersona)}>
                      <FontAwesomeIcon icon={faCircleNodes} />
                    </button>
                  </td>

                  <td style={{ width: "60px" }}>
                    <button
                      className="button is-small is-link"
                      title="Agregar domicilio desde GIS"
                      onClick={() => navegarAGisPersona(navigate, personaActual)}
                    >
                      GIS
                    </button>
                  </td>

                  <td style={{ width: "70px" }}>
                    {personaEstaActiva(personaActual) ? (
                      <FontAwesomeIcon
                        icon={faUserCheck}
                        style={{ color: "#37ff00", cursor: "pointer" }}
                        onClick={() => activarDesactivarPersona(personaActual.IdPersona, 0)}
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faUserXmark}
                        style={{ color: "#ff0000", cursor: "pointer" }}
                        onClick={() => activarDesactivarPersona(personaActual.IdPersona, 1)}
                      />
                    )}
                  </td>

                  <td style={{ width: "90px" }}>
                    <button
                      onClick={() =>
                        navigate(`/PrivateMainPage/ControlSeguimiento/${c.IdPersona}?pat=${c.IdPatologia}`)
                      }
                      style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: "16px",
                        backgroundColor: personaActual.tipo === "Caso" ? "#2563eb" : "#e63946",
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "10px",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Seguimiento
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div>No hay {subFilasNombre} para este {data.tipo?.toLowerCase()}.</div>
      )}
    </div>
  );
};

const TablaPersonas = ({
  personas,
   personasFull, // 👈 NUEVO
  tiposDocumento,
  estados,
  estadosTratamiento = [],
  fechasSeguimientoFinales,
  onEstadoChange,
  onEstadoTratamientoChange,
  onOpenModal,
  activarDesactivarPersona,
  expandedRows,
  onRowClick,
  filterText,
  rowDense
}) => {
  const navigate = useNavigate();

  const columnas = [
    { name: "Nombre", selector: (row) => row.Nombre, sortable: true, minWidth: "90px", center: true },
    { name: "Apellido", selector: (row) => row.Apellido, sortable: true, minWidth: "90px", center: true },
    { name: "NroDocumento", selector: (row) => row.NroDocumento, sortable: true, minWidth: "105px", center: true },
    { name: "Domicilio", selector: (row) => formatearDomicilio(row.Domicilio), sortable: true, minWidth: "125px" },
    { name: "Teléfono", selector: (row) => row.Telefono || "", sortable: true, minWidth: "95px", center: true },
    { name: "Eventos", selector: (row) => row.Eventos || "", sortable: true, minWidth: "170px" },
    { name: "Diagnóstico", selector: (row) => row.Diagnostico || "", sortable: true, minWidth: "145px" },
    {
      name: "Edad",
      selector: (row) => row.Edad,
      sortable: true,
      width: "56px",
      minWidth: "56px",
      maxWidth: "56px",
      grow: 0,
      center: true
    },
    {
      name: "Sexo",
      selector: (row) => row.Sexo,
      sortable: true,
      width: "62px",
      minWidth: "62px",
      maxWidth: "62px",
      grow: 0,
      center: true
    },
    {
      name: "Género",
      selector: (row) => row.Genero,
      sortable: true,
      width: "72px",
      minWidth: "72px",
      maxWidth: "72px",
      grow: 0,
      center: true
    },
    {
      name: "Fecha probable exposición",
      selector: (row) => {
        const fecha = fechasSeguimientoFinales.find((f) => f.IdPersona === row.IdPersona);
        return fecha ? formatearFecha(fecha.FechaProbableExposicion) : "No disponible";
      },
      minWidth: "142px",
    },
    {
      name: "Fecha inicio síntomas",
      selector: (row) => {
        const fecha = fechasSeguimientoFinales.find((f) => f.IdPersona === row.IdPersona);
        return fecha ? formatearFecha(fecha.FechaInicioSintomas) : "No disponible";
      },
      minWidth: "138px",
    },
    {
      name: "Fecha primera consulta",
      selector: (row) => {
        const fecha = fechasSeguimientoFinales.find((f) => f.IdPersona === row.IdPersona);
        return fecha ? formatearFecha(fecha.FechaPrimeraConsulta) : "No disponible";
      },
      minWidth: "138px",
    },

    {
      name: "Estado",
      cell: (row) => (
        <EstadoSelector
          value={row.IdEstado}
          estados={estados}
          onChange={(e) => onEstadoChange(row.IdPersona, Number(e.target.value))}
        />
      ),
      sortable: true,
      minWidth: "120px",
    },
    {
      name: "Clasificación inicio tratamiento",
      selector: (row) => row.ClasificacionInicioTratamiento || "Sin asignar",
      cell: (row) => <EstadoTratamientoColor nombre={row.ClasificacionInicioTratamiento} estadosTratamiento={estadosTratamiento} campo="CLASIFICACION_INICIO" onChange={(id) => onEstadoTratamientoChange(row.IdPersona, "clasificacion", id)} />,
      sortable: true,
      minWidth: "175px",
    },
    {
      name: "Resultado del tratamiento",
      selector: (row) => row.ResultadoTratamiento || "Sin asignar",
      cell: (row) => <EstadoTratamientoColor nombre={row.ResultadoTratamiento} estadosTratamiento={estadosTratamiento} campo="RESULTADO" onChange={(id) => onEstadoTratamientoChange(row.IdPersona, "resultado", id)} />,
      sortable: true,
      minWidth: "165px",
    },
    {
      name: "Editar",
      cell: (persona) => (
        <button
          className="button is-small is-info mr-2"
          title="Editar"
          onClick={() => onOpenModal(persona)}
        >
          <FontAwesomeIcon icon={faPencil} />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Graficar",
      cell: (persona) => (
        <button
          className="isgraphinfo mr-2"
          title="Información de Grafos"
          onClick={() => navigate("/PrivateMainPage/Grafos/" + persona.IdPersona)}
        >
          <FontAwesomeIcon icon={faCircleNodes} />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "GIS",
      cell: (persona) => (
        <button
          className="button is-small is-link mr-2"
          title="Agregar domicilio desde GIS"
          onClick={() => navegarAGisPersona(navigate, persona)}
        >
          GIS
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "74px",
    },
    {
      name: "Activar/Desactivar",
      cell: (persona) =>
        personaEstaActiva(persona) ? (
          <span
            style={{
              display: "inline-block",
              padding: "4px 12px",
              borderRadius: "20px",
              backgroundColor: "green",
              color: "white",
              fontWeight: "bold",
              fontSize: "14px",
              cursor: "pointer"
            }}
            onClick={() => activarDesactivarPersona(persona.IdPersona, 0)}
          >
            Activo
          </span>
        ) : (
          <span
            style={{
              display: "inline-block",
              padding: "4px 12px",
              borderRadius: "20px",
              backgroundColor: "red",
              color: "white",
              fontWeight: "bold",
              fontSize: "14px",
              cursor: "pointer"
            }}
            onClick={() => activarDesactivarPersona(persona.IdPersona, 1)}
          >
            Inactivo
          </span>
        ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },

    {
      name: "Seguimiento",
      cell: (row) => (
        <button
          onClick={() => navigate(`/PrivateMainPage/ControlSeguimiento/${row.IdPersona}`)}
          style={{
            display: "inline-block",
            padding: "4px 10px",
            borderRadius: "16px",
            backgroundColor: row.tipo === "Caso" ? "#2563eb" : "#e63946",
            color: "white",
            fontWeight: "bold",
            fontSize: "10px",
            border: "none",
            cursor: "pointer",
          }}
        >
          Seguimiento
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },



  ];

  const filteredData = personas.filter((item) => {
    if (!filterText) return true;
    const search = filterText.toLowerCase();
    return (
      (item.Nombre && item.Nombre.toLowerCase().includes(search)) ||
      (item.Apellido && item.Apellido.toLowerCase().includes(search)) ||
      (item.Email && item.Email.toLowerCase().includes(search)) ||
      (item.NroDocumento && item.NroDocumento.toString().includes(search)) ||
      (item.Edad && item.Edad.toString().includes(search)) ||
      (item.Sexo && item.Sexo.toLowerCase().includes(search)) ||
      (item.Eventos && item.Eventos.toLowerCase().includes(search))
    );
  });
  const customStyles = {
    table: {
      style: {
        minWidth: "1220px",
      },
    },
    rows: {
      style: {
        minHeight: "34px",
      },
    },
    headRow: {
      style: {
        minHeight: "34px",
      },
    },
    cells: {
      style: {
        paddingLeft: "4px",
        paddingRight: "4px",
        fontSize: "11px",
        lineHeight: "1.15",
        borderRight: "1px solid #ccc"
      },
    },
    headCells: {
      style: {
        paddingLeft: "2px",
        paddingRight: "2px",
        fontSize: "11px",
        lineHeight: "1.15",
        fontWeight: 600,
        borderRight: "1px solid #eee"
      },
    },
  };

  return (
    <div className="listado-tabla">
      <div className="listado-tabla__scroll">
      <DataTable
        pagination
        dense={rowDense}
        responsive={false}
        highlightOnHover
        striped
        columns={columnas}
        data={filteredData}
        customStyles={customStyles}
        expandableRows={true}
        expandableRowExpanded={(row) => expandedRows.includes(row.IdPersona)}
        onRowClicked={(row) => onRowClick(row.IdPersona)}
        noDataComponent="No hay personas para mostrar"
        expandableRowsComponent={(props) => {
          // props.data contiene la fila expandida
          const personaActualizada = personas.find(
            (p) => p.IdPersona === props.data.IdPersona
          ) || props.data;

          return (
            <FilaExpandida
              {...props}
              data={personaActualizada}
               personas={personasFull}   // 👈 USAR LISTA COMPLETA
              onEstadoChange={onEstadoChange}
              onEstadoTratamientoChange={onEstadoTratamientoChange}
              
              onOpenModal={onOpenModal}
              activarDesactivarPersona={activarDesactivarPersona}
              tiposDocumento={tiposDocumento}
              estados={estados}
              estadosTratamiento={estadosTratamiento}
              fechasSeguimientoFinales={fechasSeguimientoFinales}
            />
          );
        }}
      />
      </div>
    </div>
  );
};

export default TablaPersonas;
